/**
 * RewardService -- Calculates NXS balance and handles mission auto-completion.
 *
 * NXS balance = sum of rewardNexos from all completed missions.
 * Auto-complete: when all linked tasks of a mission are 'completed',
 * the mission status is automatically set to 'completed'.
 *
 * Uses list endpoints instead of individual getById to avoid 404s
 * on deleted tasks and reduce API calls.
 *
 * @module application/RewardService
 */

import { MissionApiClient } from '../infrastructure/api/MissionApiClient.js';
import { TaskApiClient } from '../infrastructure/api/TaskApiClient.js';
import { EventBus } from '../shared/EventBus.js';
// SEC quinta puerta (nexo-api PR 435): el panel ya no escribe NXS; la API paga al cerrar y solo el admin cierra.
import { leerCierre } from './misiones/cierreDeMision.js';

const EVENTS = {
  MISSION_COMPLETED: 'mission:completed',
  NXS_UPDATED: 'nxs:updated'
};

// Persistent notification queue (survives page reload)
const NXS_TOAST_KEY = 'nx_nxs_pending_toast';

function queueToast(missions, type = 'reward') {
  const data = { missions: missions.map(m => ({ title: m.title || m.code, nxs: m.rewardNexos || 0 })), type };
  sessionStorage.setItem(NXS_TOAST_KEY, JSON.stringify(data));
}

export function consumePendingToast() {
  const raw = sessionStorage.getItem(NXS_TOAST_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(NXS_TOAST_KEY);
  try { return JSON.parse(raw); } catch { return null; }
}

export const RewardService = {

  async calculateBalance() {
    const allMissions = await this._loadAllMissions();
    const completed = allMissions.filter(m => m.status === 'completed');
    const totalNxs = completed.reduce((sum, m) => sum + (m.rewardNexos || 0), 0);

    return {
      totalNxs,
      completedMissions: completed.length,
      totalMissions: allMissions.length,
      missions: completed.map(m => ({
        code: m.code,
        title: m.title,
        nxs: m.rewardNexos || 0
      }))
    };
  },

  async checkMissionCompletion(taskId) {
    const allMissions = await this._loadAllMissions();

    const affected = allMissions.filter(
      m => (m.taskIds || []).some(tid => String(tid) === String(taskId)) && m.status !== 'completed'
    );

    if (affected.length === 0) return { completadas: [], rechazadas: [] };

    const completedMissions = [];
    const rechazadas = [];

    for (const mission of affected) {
      if (!mission.taskIds?.length) continue;

      // Cargar cada tarea individualmente para evitar bug de paginacion (120+ tareas)
      const linkedTasks = (await Promise.all(
        mission.taskIds.map(async id => {
          const res = await TaskApiClient.getById(id);
          return res.ok ? (res.data?.data || res.data) : null;
        })
      )).filter(Boolean);

      const allDone = linkedTasks.length === mission.taskIds.length &&
        linkedTasks.every(t => t.status === 'completed');

      if (allDone) {
        const result = await MissionApiClient.update(mission.id, {
          status: 'completed'
        });

        // Los NXS los paga la API al cerrar (una vez por persona, PR 435). Si no cierra, se DICE por que.
        const cierre = leerCierre(result);
        if (cierre.cerrada) {
          completedMissions.push(mission);
          EventBus.emit(EVENTS.MISSION_COMPLETED, {
            mission,
            nxsEarned: mission.rewardNexos || 0
          });
        } else if (cierre.motivo !== 'YA_CERRADA') {
          rechazadas.push({ mission, ...cierre });
        }
      }
    }

    if (completedMissions.length > 0) {
      queueToast(completedMissions, 'reward');
      EventBus.emit(EVENTS.NXS_UPDATED);
    }

    return { completadas: completedMissions, rechazadas };
  },

  async checkMissionRevert(taskId) {
    const allMissions = await this._loadAllMissions();
    const affected = allMissions.filter(
      m => (m.taskIds || []).some(tid => String(tid) === String(taskId)) && m.status === 'completed'
    );
    if (affected.length === 0) return [];

    const reverted = [];
    for (const mission of affected) {
      const result = await MissionApiClient.update(mission.id, { status: 'in_progress' });
      if (result.ok) {
        reverted.push(mission);
      }
    }
    if (reverted.length > 0) EventBus.emit(EVENTS.NXS_UPDATED);
    return reverted;
  },

  async getLeaderboard() {
    const balance = await this.calculateBalance();
    return {
      nxs: balance.totalNxs,
      level: this._calculateLevel(balance.totalNxs),
      progress: this._levelProgress(balance.totalNxs),
      completedMissions: balance.completedMissions,
      totalMissions: balance.totalMissions
    };
  },

  _calculateLevel(nxs) {
    if (nxs >= 1000) return 5;
    if (nxs >= 500) return 4;
    if (nxs >= 200) return 3;
    if (nxs >= 50) return 2;
    return 1;
  },

  _levelProgress(nxs) {
    const thresholds = [0, 50, 200, 500, 1000];
    const level = this._calculateLevel(nxs);
    if (level >= 5) return 100;
    const current = thresholds[level - 1];
    const next = thresholds[level];
    return Math.round(((nxs - current) / (next - current)) * 100);
  },

  /**
   * Carga paginada universal — itera todas las paginas de cualquier endpoint list().
   * Evita caps silenciosos (ej: 120 tasks con pageSize=100 perdia 20).
   * @param {Function} fetcher - (page, pageSize) => Promise<{ok, data}>
   * @param {number} pageSize - items por pagina (default 200)
   */
  async _loadAllPaginated(fetcher, pageSize = 200) {
    let all = [];
    let page = 1;
    while (true) {
      const result = await fetcher(page, pageSize);
      if (!result.ok) break;
      const payload = result.data?.data || result.data;
      const items = payload?.items || [];
      all = all.concat(items);
      const totalPages = payload?.totalPages || 1;
      if (page >= totalPages || items.length === 0) break;
      page++;
    }
    return all;
  },

  async _loadAllMissions() {
    return this._loadAllPaginated((p, s) => MissionApiClient.list(p, s));
  },

  async _loadAllTasks() {
    return this._loadAllPaginated((p, s) => TaskApiClient.list(p, s));
  },

  EVENTS
};
